import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import wsService from '../../services/websocket';
import { 
  FiArrowLeft, FiMapPin, FiPackage, FiUser, FiPhone, 
  FiClock, FiCheck, FiTruck, FiRefreshCw, FiDollarSign,
  FiNavigation, FiAlertTriangle
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const ExpressDriverPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('available');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'entregador') {
      navigate('/login');
      return;
    }
    loadOrders();
  }, [user]);

  // WebSocket para novos pedidos
  useEffect(() => {
    if (!token) return;
    
    wsService.connect(token);
    
    const handleNewExpressOrder = (payload) => {
      console.log('New express order:', payload);
      toast.success('🚀 Novo pedido express disponível!');
      loadOrders();
    };
    
    wsService.addListener('new_express_order', handleNewExpressOrder);
    
    return () => {
      wsService.removeListener('new_express_order', handleNewExpressOrder);
    };
  }, [token]);

  const loadOrders = async () => {
    try {
      const [availRes, myRes] = await Promise.all([
        api.get('/express/available'),
        api.get('/express')
      ]);
      setAvailableOrders(availRes.data);
      setMyOrders(myRes.data.filter(o => !['delivered', 'returned', 'cancelled'].includes(o.status)));
    } catch (error) {
      console.error('Error loading express orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadOrders();
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      await api.post(`/express/${orderId}/accept`);
      toast.success('Pedido aceito! Vá até o endereço de coleta.');
      loadOrders();
      setActiveTab('my');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aceitar pedido');
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/express/${orderId}/status`, { status: newStatus });
      toast.success('Status atualizado!');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const handleCompleteOrder = async (orderId) => {
    const code = window.prompt('Digite o código de segurança informado pelo cliente/destinatário:');
    if (!code) return;
    
    try {
      await api.post(`/express/${orderId}/complete`, { security_code: code });
      toast.success('Entrega finalizada com sucesso!');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido ou erro ao finalizar');
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'paid': 'Aguardando coleta',
      'accepted': 'A caminho da coleta',
      'picked_up': 'Em rota de entrega',
      'returning': 'Retornando'
    };
    return labels[status] || status;
  };

  const getNextAction = (order) => {
    switch (order.status) {
      case 'paid':
      case 'accepted':
        return { label: 'Coletei o item', status: 'picked_up', icon: FiPackage };
      case 'picked_up':
        if (order.return_trip) {
          return { label: 'Entreguei - Iniciando retorno', status: 'returning', icon: FiRefreshCw };
        }
        return { label: 'Finalizar entrega', action: 'complete', icon: FiCheck };
      case 'returning':
        return { label: 'Finalizar retorno', action: 'complete', icon: FiCheck };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-red-700 text-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/entregador')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold">Pedidos Express</h1>
              <p className="text-sm text-white/70">Leva e Traz / Ponto a Ponto</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <FiRefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {/* Warning */}
      <div className="bg-red-50 border-b border-red-200 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <FiAlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700 text-sm">
            <strong>Lembre-se:</strong> NÃO transportamos alimentos ou produtos perecíveis.
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Tabs */}
        <div className="flex gap-2">
          <button 
            onClick={() => setActiveTab('available')} 
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${activeTab === 'available' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 shadow-sm'}`}
          >
            Disponíveis ({availableOrders.length})
          </button>
          <button 
            onClick={() => setActiveTab('my')} 
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${activeTab === 'my' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 shadow-sm'}`}
          >
            Meus ({myOrders.length})
          </button>
        </div>

        {/* Available Orders */}
        {activeTab === 'available' && (
          availableOrders.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <FiPackage className="mx-auto text-4xl text-gray-300 mb-3" />
              <p className="text-gray-500">Nenhum pedido express disponível</p>
              <p className="text-sm text-gray-400 mt-1">Fique atento às notificações!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {availableOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-yellow-500">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium mb-2 ${
                        order.service_type === 'leva_traz' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {order.service_type === 'leva_traz' ? '🔄 Leva e Traz' : '📍 Ponto a Ponto'}
                        {order.return_trip && ' (Ida e Volta)'}
                      </span>
                      <p className="text-sm text-gray-500">
                        <FiClock className="inline mr-1" />
                        {new Date(order.created_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <span className="text-xl font-bold text-green-600">R$ {order.driver_payment?.toFixed(2)}</span>
                  </div>
                  
                  {/* Addresses */}
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex items-start gap-2">
                      <FiMapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Coleta:</span> {order.pickup_address}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <FiMapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Entrega:</span> {order.delivery_address}
                      </div>
                    </div>
                  </div>
                  
                  {/* Item */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <FiPackage className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{order.item_description}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleAcceptOrder(order.id)}
                    className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FiTruck className="w-5 h-5" />
                    Aceitar Pedido
                  </button>
                </div>
              ))}
            </div>
          )
        )}

        {/* My Orders */}
        {activeTab === 'my' && (
          myOrders.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <FiTruck className="mx-auto text-4xl text-gray-300 mb-3" />
              <p className="text-gray-500">Você não tem pedidos express ativos</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myOrders.map((order) => {
                const nextAction = getNextAction(order);
                
                return (
                  <div key={order.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-red-600">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="inline-block px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium mb-2">
                          {getStatusLabel(order.status)}
                        </span>
                        <p className="text-sm text-gray-500">
                          {order.service_type === 'leva_traz' ? '🔄 Leva e Traz' : '📍 Ponto a Ponto'}
                        </p>
                      </div>
                      <span className="text-xl font-bold text-green-600">R$ {order.driver_payment?.toFixed(2)}</span>
                    </div>
                    
                    {/* Addresses */}
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex items-start gap-2">
                        <FiMapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Coleta:</span> {order.pickup_address}
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <FiMapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Entrega:</span> {order.delivery_address}
                        </div>
                      </div>
                    </div>
                    
                    {/* Recipient */}
                    <div className="bg-blue-50 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FiUser className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">{order.recipient_name}</span>
                        </div>
                        <a 
                          href={`tel:${order.recipient_phone}`}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <FiPhone className="w-4 h-4" />
                          {order.recipient_phone}
                        </a>
                      </div>
                    </div>
                    
                    {/* Item */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <div className="flex items-start gap-2">
                        <FiPackage className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-700">{order.item_description}</p>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    {nextAction && (
                      <button
                        onClick={() => {
                          if (nextAction.action === 'complete') {
                            handleCompleteOrder(order.id);
                          } else {
                            handleUpdateStatus(order.id, nextAction.status);
                          }
                        }}
                        className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <nextAction.icon className="w-5 h-5" />
                        {nextAction.label}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </main>
    </div>
  );
};

export default ExpressDriverPage;
